import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { Modal } from '../../ui/Modal';
import { todayLocalIso } from '../../shared/dates';
import { useCohorts } from '../../shared/cohorts';
import { ServiceLabels, type ServiceType } from '@census/shared';

/**
 * Step-up / step-down workflow. Used when a client moves between programs
 * (e.g. PHP → IOP after stabilising, or IOP → OP for outpatient continuity).
 *
 * What happens server-side when the operator clicks Transition:
 *   1. The current ClientCohortAssignment row's endDate is set to
 *      (transitionDate - 1 day).
 *   2. A new ClientCohortAssignment row opens at transitionDate.
 *   3. Client.cohort / cohortNumber / cohortId / requiredServices are updated.
 *   4. The schedule rebuild runs — old group enrollments end yesterday,
 *      new ones are placed, and future SCHEDULED instances are regenerated
 *      for the new program.
 *
 * The whole thing is one SERIALIZABLE transaction (cohortNumber races,
 * concurrent transitions on the same client). The rebuild runs after the
 * commit so a rebuild failure leaves the cohort change in place and the
 * operator can re-run rebuild manually.
 */
export function CohortTransitionModal({
  client,
  onClose,
  onTransitioned,
}: {
  client: any;
  onClose: () => void;
  onTransitioned: () => void;
}) {
  const { cohorts: cohortList } = useCohorts({ activeOnly: true });
  // Default the picker to the first cohort that isn't the client's current one
  const candidates = useMemo(
    () => cohortList.filter((c) => c.code !== client.cohort),
    [cohortList, client.cohort],
  );
  const [newCohort, setNewCohort] = useState<string>(candidates[0]?.code ?? '');
  const [transitionDate, setTransitionDate] = useState<string>(todayLocalIso());
  const [reason, setReason] = useState<string>('');
  const [pinIopTrack, setPinIopTrack] = useState<string>('');

  // For IOP-typed target cohorts, let the admin pin a specific track.
  // The list of IOP track resources is the same one the intake wizard uses.
  const { data: iopTracks } = useQuery({
    queryKey: ['iop-track-resources'],
    queryFn: () =>
      api
        .get('/resources', { params: { type: 'GROUP', activeOnly: 'true' } })
        .then((r) => (r.data as any[]).filter((g) => g.groupServiceType === 'IOP')),
  });
  const targetNeedsIopTrack = useMemo(() => {
    const row = cohortList.find((c) => c.code === newCohort);
    return (row?.requiredGroups ?? []).includes('IOP');
  }, [cohortList, newCohort]);

  const transition = useMutation({
    mutationFn: () =>
      api
        .post(`/clients/${client.id}/transition`, {
          newCohort,
          transitionDate,
          reason: reason.trim() || undefined,
          iopTrackResourceId: targetNeedsIopTrack ? pinIopTrack || null : null,
        })
        .then((r) => r.data),
    onSuccess: () => onTransitioned(),
  });

  const newCohortRow = cohortList.find((c) => c.code === newCohort);
  const errMsg =
    (transition.error as any)?.response?.data?.message ?? null;

  return (
    <Modal open={true} onClose={onClose} title={`Change cohort — ${client.firstName} ${client.lastName}`} size="lg">
      <div className="space-y-4">
        <div className="rounded-md border border-line-subtle bg-surface-subtle/60 p-3 text-sm">
          <div className="font-medium text-ink">Current cohort</div>
          <div className="text-ink-subtle">
            <span className="font-mono text-brand-700">{client.cohortId}</span> · {client.cohort} — {/* */}
            {cohortList.find((c) => c.code === client.cohort)?.name ?? client.cohort}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label">New cohort</label>
            <select
              className="input"
              value={newCohort}
              onChange={(e) => setNewCohort(e.target.value)}
            >
              {candidates.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Transition date</label>
            <input
              type="date"
              className="input"
              value={transitionDate}
              min={todayLocalIso()}
              onChange={(e) => setTransitionDate(e.target.value)}
            />
            <p className="mt-1 text-2xs text-ink-subtle">
              Must be today or in the future. Current cohort closes the day before; new cohort opens on this date. Backdated transitions would create ghost past instances and are refused.
            </p>
          </div>
          {targetNeedsIopTrack && (
            <div className="sm:col-span-2">
              <label className="label">IOP track (optional — engine picks by capacity if blank)</label>
              <select
                className="input"
                value={pinIopTrack}
                onChange={(e) => setPinIopTrack(e.target.value)}
              >
                <option value="">— Let engine pick —</option>
                {(iopTracks ?? []).map((t: any) => (
                  <option key={t.id} value={t.id}>
                    {t.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="label">Reason (optional)</label>
            <input
              className="input"
              placeholder="e.g. Stabilized; stepping down from PHP to IOP"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        {newCohortRow && (
          <div className="rounded-md border border-brand-200 bg-brand-50/40 p-3 text-xs">
            <div className="mb-1 font-semibold text-brand-800">After transition</div>
            <div className="mb-1">
              <span className="text-ink-subtle">Required groups: </span>
              {newCohortRow.requiredGroups.length === 0 ? (
                <span className="text-ink-subtle">— none defined —</span>
              ) : (
                newCohortRow.requiredGroups.map((g) => (
                  <span key={g} className="mr-1 inline-block rounded bg-white px-1.5 py-0.5 text-2xs ring-1 ring-brand-200">
                    {ServiceLabels[g as ServiceType] ?? g}
                  </span>
                ))
              )}
            </div>
            <div>
              <span className="text-ink-subtle">Required individual services: </span>
              {newCohortRow.defaultIndividualServices.length === 0 ? (
                <span className="text-ink-subtle">— none —</span>
              ) : (
                newCohortRow.defaultIndividualServices.map((s) => (
                  <span key={s} className="mr-1 inline-block rounded bg-white px-1.5 py-0.5 text-2xs ring-1 ring-emerald-200">
                    {ServiceLabels[s as ServiceType] ?? s}
                  </span>
                ))
              )}
            </div>
            <p className="mt-2 text-ink-subtle">
              Old group enrollments end {transitionDate ? `on ${transitionDate}` : 'on transition date'};
              new ones are placed by the schedule rebuild. Future SCHEDULED instances are regenerated.
            </p>
          </div>
        )}

        {errMsg && (
          <div className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{errMsg}</div>
        )}
      </div>
      <footer className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose} disabled={transition.isPending}>
          Cancel
        </button>
        <button
          className="btn-primary"
          disabled={transition.isPending || !newCohort || !transitionDate}
          onClick={() => transition.mutate()}
        >
          {transition.isPending ? 'Transitioning…' : 'Transition & rebuild'}
        </button>
      </footer>
    </Modal>
  );
}
